import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '~/lib/utils';

/**
 * Whether the nearest `PageShell`/`PageHeading` is rendering inside a
 * settings-family modal pane rather than as a standalone top-level route
 * (FRONT-2376). `System Settings` reuses the eight former sidebar pages
 * verbatim — same component, same behaviour, same queries — inside its
 * modal's per-section pane, so each page's own chrome has to shrink to fit a
 * pane instead of a full viewport. A context rather than a prop: the pages
 * themselves (`routes/policy.tsx`, `routes/admin-sandbox.tsx`, …) render the
 * exact same component tree whether they are reached directly or through the
 * modal — their own eight bare paths (`/policy`, `/admin/sandbox`, …) are
 * legacy redirects into `/system-settings` now, not standalone routes — so
 * nothing at their own call site can pass a prop down into `PageShell` — the
 * modal wraps them in `<PageShellEmbedProvider>` instead, and every
 * `PageShell`/`PageHeading` under that provider reads it.
 */
const PageShellEmbedContext = createContext(false);

/** Wrap page content that is being rendered inside a settings-family modal
 *  pane. See {@link PageShellEmbedContext}. */
export function PageShellEmbedProvider({ children }: { children: ReactNode }) {
  return <PageShellEmbedContext.Provider value={true}>{children}</PageShellEmbedContext.Provider>;
}

/** Read whether the nearest `PageShell`/`PageHeading` is embedded — exported
 *  so `page-heading.tsx` can answer the same question without importing a
 *  second copy of the context. */
export function usePageShellEmbedded(): boolean {
  return useContext(PageShellEmbedContext);
}

interface PageShellProps {
  /** Page title (left side of the header strip). */
  title?: ReactNode;
  /** Short one-liner shown under the title in muted text. Optional. */
  description?: ReactNode;
  /** Icon shown left of the title. Optional. */
  icon?: ReactNode;
  /** Inline badge or chip rendered immediately after the title (e.g. "12 pending"). */
  badge?: ReactNode;
  /** Right-side header slot — typically action buttons. */
  actions?: ReactNode;
  /**
   * Full custom header. This is now the CANONICAL shape for a top-level page:
   * pass `<PageHeading>` (plus any action buttons beside it) here rather than
   * hand-rolling one. The structured `title`/`icon`/`description` props above
   * are the compact bar, kept for detail views (an open file, a cron's editor)
   * and for pages that haven't adopted the page header. When provided, this
   * overrides them.
   */
  header?: ReactNode;
  /** Optional sub-strip rendered immediately under the header (e.g. tabs row). */
  subHeader?: ReactNode;
  /** Scrollable page body. */
  children: ReactNode;
  /** Extra classes on the scroll body's *inner* container. Default: `px-5 py-4`. */
  bodyClassName?: string;
  /**
   * Disable the default container constraint on the scroll body — use when
   * a page needs custom inner width but still wants the chrome to bleed.
   */
  noBodyContainer?: boolean;
  /**
   * Render header, sub-header and body at the full available width instead of
   * Tailwind's centred `container`. Header and body stay aligned (both go
   * full-width together), so there's no right-edge mismatch.
   */
  fullWidth?: boolean;
  /**
   * Make the body fill the viewport instead of scrolling as a whole. On `lg+`
   * the body becomes a non-scrolling flex column so an app-like page (e.g. the
   * app editor) can own a bounded height and scroll its own inner panes —
   * avoiding a redundant outer page scrollbar. On small screens the body still
   * scrolls normally so tall stacked content stays reachable.
   */
  fillBody?: boolean;
  /**
   * A 3px colour rule across the very top of the header band, edge to edge
   * whatever the content width (FRONT-2488). A project's identity accent —
   * it lives here so a page can keep the standard centred column and still
   * have a rule that spans the whole strip, which used to force `fullWidth`
   * plus negative margins reaching into this shell's own padding.
   */
  headerAccent?: string;
}

/**
 * Standard route shell: full-bleed header + full-bleed scroll area with an
 * always-visible scrollbar, both wrapping content in Tailwind's `container`
 * so the reading column stays centred on wide screens.
 *
 * Two header modes:
 *
 *   <PageShell icon={…} title="Security" description="…" actions={…}>
 *     …content…
 *   </PageShell>
 *
 *   <PageShell header={<custom JSX>}> …content… </PageShell>
 *
 * Pages with their own complex layout (chat, home) skip this and apply
 * `bleed-x` + `scrollbar-visible` directly.
 *
 * A THIRD mode, embedded (FRONT-2376): rendered inside a
 * `<PageShellEmbedProvider>` (the System Settings modal wraps each of its
 * panels in one), the full-bleed chrome drops to a compact panel header —
 * see {@link PageShellEmbedContext}'s own docblock.
 */

/** The structured-props header body (icon/title/badge/description/actions) —
 *  shared between the embedded and standalone header bands below, which
 *  otherwise differ only in the chrome WRAPPING it (see `PageShell`). */
function PageShellDefaultHeader({
  icon,
  title,
  badge,
  description,
  actions,
}: Pick<PageShellProps, 'icon' | 'title' | 'badge' | 'description' | 'actions'>) {
  return (
    <>
      <div className="flex items-start gap-2 min-w-0">
        {icon && <div className="text-muted-foreground mt-0.5 shrink-0">{icon}</div>}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {title && <h1 className="font-semibold text-foreground">{title}</h1>}
            {badge}
          </div>
          {description && (
            // A div, not a <p>: `description` is a ReactNode and a page may
            // pass block-level content, which is invalid inside a paragraph
            // (FRONT-1089). Keep the div even if every current caller passes
            // inline content — the prop's type still allows block content,
            // so the next caller can reintroduce it.
            <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
          )}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
    </>
  );
}

export function PageShell({
  title,
  description,
  icon,
  badge,
  actions,
  header,
  subHeader,
  children,
  bodyClassName,
  noBodyContainer,
  fullWidth,
  fillBody,
  headerAccent,
}: PageShellProps) {
  const embedded = usePageShellEmbedded();
  const hasHeader = header || title || icon || description || badge || actions;
  const containerCls = fullWidth || embedded ? 'w-full' : 'container mx-auto';

  // FRONT-2376: embedded (inside the System Settings modal) drops the
  // full-page chrome — the full-bleed border/blur strip, the outer page
  // padding — down to a compact panel header, because the modal pane is
  // already a bounded box with its own padding, not a full viewport. The DOM
  // shape stays the same (header, optional subHeader, body, in that order)
  // so a page's own internal layout (tabs, actions) is unaffected; only the
  // chrome AROUND it shrinks.
  return (
    <div className={cn('flex flex-col flex-1 min-h-0', !embedded && 'bg-card')}>
      {/*
       * Header + subheader live INSIDE the scroll container, so they scroll
       * away with the page content instead of staying pinned to the viewport
       * (FRONT-1458 — un-pinned deliberately; they used to be `sticky`).
       */}
      <div
        className={cn(
          embedded ? 'flex-1 overflow-y-auto scrollbar-visible' : 'bleed-x flex-1 overflow-y-auto scrollbar-visible',
          fillBody && 'min-h-0 lg:overflow-hidden lg:flex lg:flex-col',
        )}
      >
        {hasHeader && (
          embedded ? (
            <div>
              <div
                className={cn(
                  containerCls,
                  'px-1 pb-3 mb-3 border-b border-border flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3',
                )}
              >
                {header ?? <PageShellDefaultHeader {...{ icon, title, badge, description, actions }} />}
              </div>
            </div>
          ) : (
            // Literal string class (not `cn(...)`) on purpose: `tests/page-
            // chrome.meta.test.ts` finds this exact chrome band by scanning
            // for a plain `<div className="…">` carrying this signature, to
            // catch a future `sticky`/`fixed` creeping back onto it — a
            // dynamic class here would make that guard blind to this band.
            <div className="bleed-x z-20 border-b border-border bg-card/60 backdrop-blur-md backdrop-saturate-150">
              {headerAccent && <span className="block h-[3px]" style={{ background: headerAccent }} aria-hidden />}
              <div className={cn(containerCls, 'px-5 py-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3')}>
                {header ?? <PageShellDefaultHeader {...{ icon, title, badge, description, actions }} />}
              </div>
            </div>
          )
        )}
        {subHeader && (
          embedded ? (
            <div>
              <div className={cn(containerCls, 'px-1')}>{subHeader}</div>
            </div>
          ) : (
            // Same literal-string requirement as the header band above.
            <div className="bleed-x z-10 border-b border-border bg-card/60 backdrop-blur-md backdrop-saturate-150">
              <div className={cn(containerCls, 'px-5')}>{subHeader}</div>
            </div>
          )
        )}
        {noBodyContainer ? (
          children
        ) : (
          <div
            className={cn(
              containerCls,
              embedded ? 'px-1 py-3' : 'px-5 py-4',
              fillBody && 'lg:flex-1 lg:min-h-0 lg:flex lg:flex-col lg:min-w-0',
              bodyClassName,
            )}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
